import { useState } from 'react';
import { FiX, FiCopy, FiEye, FiEyeOff } from 'react-icons/fi';
import { useI18n } from '../../hooks/useI18n';

export function PrivateKeyModal({ open, onClose, privateKey, pushToast }) {
  const { t } = useI18n();
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(privateKey).then(() => {
      setCopied(true);
      pushToast?.(t('wallet.privateKeyCopied'), 'success');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-surface-highlight rounded-2xl border border-glass-border shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-glass-border">
          <h3 className="text-lg font-bold text-white">{t('wallet.exportPrivateKey')}</h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-surface-highlight transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Warning */}
          <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl">
            <h4 className="text-sm font-bold text-rose-400 mb-2">{t('wallet.privateKeyWarningTitle')}</h4>
            <p className="text-xs text-rose-300/80 leading-relaxed">
              {t('wallet.privateKeyWarningLine1')}
              <br />
              {t('wallet.privateKeyWarningLine2')}
            </p>
          </div>

          <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
            <h4 className="text-sm font-bold text-amber-400 mb-2">{t('wallet.privateKeyOwnershipTitle')}</h4>
            <p className="text-xs text-amber-200/80 leading-relaxed">
              {t('wallet.privateKeyOwnershipDesc')}
            </p>
          </div>

          {/* Private Key */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-400">{t('wallet.yourPrivateKey')}</label>
            <div className="relative group">
              <div
                className={`w-full p-4 pr-24 bg-surface border border-glass-border rounded-xl font-mono text-sm break-all transition-colors ${
                  showKey
                    ? 'text-gray-200'
                    : 'text-gray-600 select-none blur-[2px] group-hover:blur-none group-hover:text-gray-500'
                }`}
              >
                {showKey
                  ? privateKey
                  : '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••'}
              </div>

              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <button
                  onClick={() => setShowKey(!showKey)}
                  className="p-2 rounded-lg bg-surface hover:bg-surface-highlight text-gray-400 hover:text-white transition-colors border border-glass-border"
                  title={showKey ? t('wallet.hide') : t('wallet.show')}
                >
                  {showKey ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
                <button
                  onClick={handleCopy}
                  className="p-2 rounded-lg bg-accent/10 hover:bg-accent/20 text-accent hover:text-accent-soft transition-colors border border-accent/20"
                  title={t('wallet.copy')}
                >
                  {copied ? (
                    <span className="text-xs font-bold">{t('wallet.copiedShort')}</span>
                  ) : (
                    <FiCopy size={16} />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-glass-border flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-surface-highlight hover:bg-surface text-white text-sm font-medium transition-colors"
          >
            {t('wallet.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
